import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag } from '@carbon/react';
import {
  Analytics,
  CloudMonitoring,
  Notification,
  Security,
  DataBase,
  ChartLine,
  MobileCheck,
  Temperature,
  Humidity,
  BatteryFull,
  ArrowRight,
  CheckmarkFilled,
  ShoppingCart,
  WatsonHealthAiResults,
  LocationCompany,
  Certificate,
  QrCode,
  Delivery,
  TreeView,
  DocumentExport,
  Printer,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../hooks/useProducts';
import PipelineIcon from '../components/icons/PipelineIcon';
import LandingShowcaseSlider from '../components/LandingShowcaseSlider';
import appleBadge from '../assets/apple.webp';
import googlePlayBadge from '../assets/Google_Play_Store_badge_EN.svg.webp';

const SENSOR_SHOP_MATCHERS = {
  s6: (product) => {
    const name = (product.name || '').toLowerCase();
    const model = (product.model || '').toLowerCase();
    return (name.includes('s6') || model.includes('s6')) && !name.includes('s6t') && !model.includes('s6t');
  },
  gateway: (product) => product.category === 'gateway' || product.sensor_type === 'gateway',
  ela: (product) => product.sensor_type === 'ela' || /ela|puck/i.test(product.name || ''),
  kbeacon: (product) => product.sensor_type === 'kbeacon' || /kbeacon/i.test(product.name || ''),
  blogm2: (product) => product.sensor_type === 'blogm2' || /blogm2|b-logm2/i.test(product.name || ''),
  blogmp2: (product) => product.sensor_type === 'blogmp2' || /blogmp|blogm p2/i.test(product.name || ''),
};

const resolveSensorShopUrl = (products, shopMatch) => {
  const matcher = SENSOR_SHOP_MATCHERS[shopMatch];
  if (!matcher || !products?.length) return '/shop';
  const product = products.find(matcher);
  return product ? `/product/${product.id}` : '/shop';
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { products } = useProducts('all');
  const [revealedSensor, setRevealedSensor] = useState(null);

  useEffect(() => {
    if (!revealedSensor) return undefined;
    const close = () => setRevealedSensor(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [revealedSensor]);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const bgDark = isDark ? '#0f0f0f' : '#e8e8e8'; // Darker alternate background
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  const byoBg = isDark ? '#262626' : '#393939';
  const byoBgHover = isDark ? '#161616' : '#262626';
  const byoText = '#ffffff';
  const byoDesc = 'rgba(255, 255, 255, 0.85)';

  const mainFeatures = [
    {
      icon: Temperature,
      title: 'Temperature Monitoring',
      description: 'Track temperature in real-time across all your assets and locations',
      details: ['High-precision sensors', 'Custom threshold alerts', 'Historical data analysis'],
    },
    {
      icon: Humidity,
      title: 'Humidity Tracking',
      description: 'Monitor humidity levels to ensure optimal storage conditions',
      details: ['0-100% range', 'Dew point calculations', 'Trend analysis'],
    },
    {
      icon: ChartLine,
      title: 'Live Visualization',
      description: 'Interactive charts and dashboards for real-time monitoring',
      details: ['Real-time updates', 'Custom date ranges', 'Export capabilities'],
    },
    {
      icon: WatsonHealthAiResults,
      title: 'AI-Powered Insights',
      description: 'Get intelligent recommendations and predictive analytics',
      details: ['Anomaly detection', 'Predictive alerts', 'Optimization suggestions'],
    },
    {
      icon: Notification,
      title: 'Smart Alerts',
      description: 'Receive instant notifications when thresholds are breached',
      details: ['Push notifications', 'SMS alerts', 'Email reports'],
    },
    {
      icon: LocationCompany,
      title: 'Zone Management',
      description: 'Organize sensors by zones for better management',
      details: ['Custom zones', 'Zone-level reporting', 'Access controls'],
    },
  ];

  const benefits = [
    { icon: Analytics, title: 'Analytics', desc: 'Dashboards and insights for temperature and humidity trends' },
    { icon: DataBase, title: 'Flexible Integration', desc: 'Bring your own devices or use ours with simple onboarding' },
    { icon: CloudMonitoring, title: 'Intelligent Tracking', desc: 'Track assets and locations in real-time across operations' },
    { icon: Notification, title: 'Smart Alerts', desc: 'Notify teams on threshold breaches and status changes instantly' },
  ];

  const gpsTrackingRows = [
    {
      tag: 'Live GPS',
      tagType: 'cyan',
      title: 'Never lose sight of a shipment',
      description: 'Follow every trip on the map with live GPS plus temperature and humidity in the same view — anywhere, any day, any time.',
      image: '/images/feature-2.webp',
      alt: 'ChillVerify app showing live GPS tracking with temperature',
      color: '#0072c3',
      hoverColor: '#00539a',
      bullets: [
        'Real-time location on the road or in the yard',
        'Temperature and humidity recorded with every GPS point',
        'Start, pause and complete trips from the app',
      ],
    },
    {
      tag: 'KGateway',
      tagType: 'purple',
      title: 'KGateway includes a GPS module',
      description: 'The KGateway enclosure has an onboard GPS module, so location is captured at the gateway itself and streamed with your sensor readings.',
      image: '/assets/ca3fa4424957a6232681d8abcf39a7c3.webp',
      alt: 'KGateway hardware with onboard GPS module',
      color: '#6929c4',
      hoverColor: '#491d8b',
      bullets: [
        'Built-in GPS — no extra tracker required',
        'Location, temperature and humidity in one uplink',
        'Works for trucks, mobile coolers and remote sites',
      ],
    },
  ];

  const gs1QrRows = [
    {
      tag: 'GS1 Handoff',
      tagType: 'teal',
      title: 'QR code GS1 handoff',
      description: 'Scan the GS1 QR on the pallet label to identify the shipment, confirm the authorised recipient, and record custody in the EPCIS chain.',
      image: '/assets/qr-code-verification.webp',
      alt: 'ChillVerify QR codes used to scan and hand off GS1 shipments',
      color: '#007d79',
      hoverColor: '#005d5d',
      bullets: [
        'Scan the pallet QR to look up SSCC, GTIN and EPC',
        'Confirm the authorised sender, carrier or receiver',
        'Record the handoff with temperature still attached',
      ],
    },
    {
      tag: 'Brother Printer',
      tagType: 'blue',
      title: 'Print QR labels on the fly',
      description: 'Print QR labels on the fly and paste them onto pallets — great for self-service printing directly from your device. ChillVerify – Brother Bluetooth connectivity built in — no extra dongle or cable.',
      image: '/assets/brother-ql-1110nwb-nobg.png',
      alt: 'Brother QL-1110NWB QR label printer with built-in Bluetooth',
      color: '#0f62fe',
      hoverColor: '#0043ce',
      bullets: [
        'Print labels on the fly, directly from your device',
        'Paste QR labels onto pallets in seconds',
        'ChillVerify – Brother Bluetooth connectivity built in — no extra dongle or cable',
      ],
    },
  ];

  const sensors = [
    {
      name: 'Sensor S6',
      shopMatch: 's6',
      type: 'Temperature & humidity logger',
      description: 'Wireless sensor for fridges, cool rooms and display cases. Streams live readings to your ChillVerify dashboard.',
      image: '/assets/S6.webp',
      color: '#0072c3',
      hoverColor: '#00539a',
    },
    {
      name: 'Gateway',
      shopMatch: 'gateway',
      type: 'MQTT cloud gateway',
      description: 'Collects data from nearby BLE sensors and publishes it securely to the cloud so your team sees everything in one place.',
      image: '/assets/ca3fa4424957a6232681d8abcf39a7c3.webp',
      color: '#6929c4',
      hoverColor: '#491d8b',
    },
    {
      name: 'ELA Blue PUCK',
      shopMatch: 'ela',
      type: 'EN12830 data logger',
      description: 'Certified temperature logger for pharma and food cold chain. Built for audit-ready compliance and long-term storage records.',
      image: '/assets/ELA_Blue_PUCK_T_EN12830.webp',
      color: '#007d79',
      hoverColor: '#005d5d',
    },
    {
      name: 'KBeacon',
      shopMatch: 'kbeacon',
      type: 'BLE temperature beacon',
      description: 'Compact beacon for real-time temperature monitoring via the ChillVerify app or gateway — ideal for mobile zones and walk-in coolers.',
      image: '/assets/1-Black-KBeacon.webp',
      color: '#1192e8',
      hoverColor: '#0072c3',
    },
    {
      name: 'BLogm2',
      shopMatch: 'blogm2',
      type: 'BLE temperature logger',
      description: 'Low-profile logger for shelves, cabinets and small storage spaces. Simple setup with automatic sync when in range.',
      image: '/assets/blogm.webp',
      color: '#009d9a',
      hoverColor: '#007d79',
    },
    {
      name: 'BLogmp2',
      shopMatch: 'blogmp2',
      type: 'BLE logger (extended)',
      description: 'Enhanced BLE logger with longer battery life and reliable offline storage — readings upload when connectivity returns.',
      image: '/assets/blogm2.webp',
      color: '#0043ce',
      hoverColor: '#002d9c',
    },
  ];

  const sensorShopUrls = useMemo(() => (
    Object.fromEntries(
      sensors.map((sensor) => [sensor.shopMatch, resolveSensorShopUrl(products, sensor.shopMatch)])
    )
  ), [products]);

  const handleSensorCardClick = (sensor, shopUrl, event) => {
    if (event.target.closest('.hardware-card__buy-btn')) return;
    event.stopPropagation();
    if (revealedSensor !== sensor.name) {
      setRevealedSensor(sensor.name);
      return;
    }
    navigate(shopUrl);
  };

  const handleSensorBuyNow = (shopUrl, event) => {
    event.stopPropagation();
    navigate(shopUrl);
  };

  const appShowcaseRows = [
    {
      tag: 'Dashboard',
      tagType: 'blue',
      title: 'Intuitive web dashboard',
      description: 'Visualise your cold chain and playback temperature changes in realtime on our web dashboards. Monitor live temperature, humidity, sensors and zones from one clear view.',
      image: '/images/dashboard-screenshot.webp',
      alt: 'Dashboard screenshot showing cold chain monitoring',
      bullets: ['Live temperature and humidity monitoring', 'Interactive charts and graphs', 'Multi-zone dashboard views'],
    },
    {
      tag: 'Zones',
      tagType: 'purple',
      title: 'Create zones for every use case',
      description: 'Organize sensors by warehouses, cold storage, vehicles, offices or any monitored space. Each zone can have its own sensors, alert rules and reporting workflow.',
      image: '/assets/CreateNewZones.webp',
      alt: 'Create zones for different use cases',
      bullets: ['Custom zones for locations and vehicles', 'Zone-level alerts and reporting', 'Simple setup for new monitoring areas'],
    },
    {
      tag: 'Sensors',
      tagType: 'cyan',
      title: 'Easy sensor setup',
      description: 'Add and configure sensors quickly with a guided flow. Assign devices to zones, name them clearly and get monitoring started without a complex setup process.',
      image: '/assets/Add Sensor.webp',
      alt: 'Add sensor setup screen',
      bullets: ['Fast sensor onboarding', 'Zone assignment during setup', 'Clear sensor naming and configuration'],
    },
    {
      tag: 'Analytics',
      tagType: 'teal',
      title: 'Live data visualization',
      description: 'Turn sensor readings into clear charts and trends. Review temperature and humidity history, compare changes over time and spot issues before they become costly.',
      image: '/assets/SensorChart.webp',
      alt: 'Sensor chart visualization',
      bullets: ['Real-time and historical charts', 'Temperature and humidity trends', 'Export-ready monitoring insights'],
    },
    {
      tag: 'AI',
      tagType: 'magenta',
      title: 'AI-powered insights',
      description: 'Use AI to analyse patterns, explain anomalies and generate recommendations from your monitoring data so teams can make faster decisions.',
      image: '/assets/AiAnalysis.webp',
      alt: 'AI analysis screen',
      bullets: ['Pattern and anomaly detection', 'AI-assisted reporting', 'Recommendations from monitoring data'],
    },
    {
      tag: 'Reports',
      tagType: 'blue',
      title: 'Trip reports and tracking controls',
      description: 'Create reports for mobile zones, track delivery events and record GPS, temperature and humidity data through each step of a shipment.',
      image: '/images/feature-2.webp',
      alt: 'Trip reports and tracking controls',
      bullets: ['Start, pause and stop tracking events', 'GPS and temperature records', 'Customer-ready shipment reports'],
    },
    {
      tag: 'Alerts',
      tagType: 'red',
      title: 'Role-based alert rules',
      description: 'Create custom rules for fridges, freezers, rooms and vehicles so the right people are notified when temperatures move outside safe limits.',
      image: '/assets/freezerrule-removebg-preview.webp',
      alt: 'Alert rule configuration',
      bullets: ['Custom temperature thresholds', 'SMS, push and email notifications', 'Rules by sensor, role or zone'],
    },
    {
      tag: 'Verification',
      tagType: 'green',
      title: 'Generate reports and QR codes',
      description: 'Generate verification reports with QR codes so clients, partners and auditors can quickly view trusted shipment or storage records.',
      image: '/assets/qr-code-verification.webp',
      alt: 'QR Code Verification',
      bullets: ['Unique QR codes for reports', 'Shareable verification pages', 'Print-ready shipment labels'],
    },
    {
      tag: 'Offline Sync',
      tagType: 'purple',
      title: 'Offline data collection',
      description: 'Continue collecting readings when sensors are offline, then sync stored data when connectivity returns so monitoring history remains complete.',
      image: '/assets/KBeaconSyncOffline.webp',
      alt: 'KBeacon offline sync screen',
      bullets: ['Stored readings during outages', 'Automatic sync on reconnect', 'Reduced risk of missing data'],
    },
  ];

  const splitEpcisEventType = (eventType) => {
    if (eventType === 'EPCISDocument') return ['EPCIS', 'Document'];
    if (eventType.endsWith('Event')) return [eventType.replace(/Event$/, ''), 'Event'];
    return [eventType, ''];
  };

  const epcisTraceabilitySteps = [
    {
      step: '01',
      eventType: 'ObjectEvent',
      title: 'GS1 identity at origin',
      description: 'Assign SSCC, GTIN, lot and expiry when a shipment starts. Every trip gets a unique EPC URI used across all downstream events.',
      icon: QrCode,
      color: '#0072c3',
      hoverColor: '#00539a',
    },
    {
      step: '02',
      eventType: 'ObjectEvent',
      title: 'Live sensor ObjectEvents',
      description: 'Temperature and humidity readings attach to the shipment EPC in transit so excursions are tied to the exact product identity.',
      icon: Temperature,
      color: '#007d79',
      hoverColor: '#005d5d',
    },
    {
      step: '03',
      eventType: 'TransactionEvent',
      title: 'Custody handoffs',
      description: 'Record bizStep transitions as product moves from sender to carrier to receiver — a clear chain of possession for audits.',
      icon: Delivery,
      color: '#6929c4',
      hoverColor: '#491d8b',
    },
    {
      step: '04',
      eventType: 'AggregationEvent',
      title: 'Parent / child aggregation',
      description: 'Pack child shipments into parent SSCCs with standard EPCIS AggregationEvent ADD and DELETE when pallets are split or recombined.',
      icon: TreeView,
      color: '#1192e8',
      hoverColor: '#0072c3',
    },
    {
      step: '05',
      eventType: 'EPCISDocument',
      title: 'Export & verification',
      description: 'Download EPCIS 2.0 JSON-LD or share QR-linked verification reports so partners and regulators can confirm the full cold chain record.',
      icon: DocumentExport,
      color: '#0043ce',
      hoverColor: '#002d9c',
    },
  ];

  const industryShowcaseRows = [
    {
      tag: 'Fresh Food & Butchers',
      tagType: 'green',
      title: 'Protect meat and deli products from temperature abuse',
      description: 'Keep fridges, freezers and display cabinets within safe limits so your customers always get fresh, high‑quality product.',
      image: '/assets/4761.webp',
      alt: 'Fresh food counter in a butcher shop',
      bullets: [
        'Wireless sensors in cool rooms, prep areas and service counters',
        'Instant alerts when temperatures drift out of HACCP limits',
        'Automatic logs to prove compliance during food safety audits',
      ],
    },
    {
      tag: 'Greenhouses',
      tagType: 'green',
      title: 'Keep growing conditions stable for high‑value crops',
      description: 'Monitor temperature and humidity across bays so plants stay within tight environmental ranges all day and overnight.',
      image: '/assets/front-view-woman-picking-tomatoes.webp',
      alt: 'Greenhouse with vegetables being picked',
      bullets: [
        'Zone sensors across greenhouse rows and propagation areas',
        'Trend dashboards to spot hot spots and humidity swings early',
        'Alert rules for overnight temperature drops or fan failures',
      ],
    },
    {
      tag: 'Supermarkets & Retailers',
      tagType: 'teal',
      title: 'Monitor every fridge, freezer and display cabinet',
      description: 'Reduce shrink and protect brand reputation by continuously monitoring cold chain equipment in‑store.',
      image: '/assets/local-retailer-taking-care-his-business.webp',
      alt: 'Local retailer checking produce',
      bullets: [
        'Multi‑zone dashboards for fresh, dairy, frozen and grab‑and‑go cases',
        'Store‑level alert routing to the right staff on shift',
        'Audit‑ready temperature history for QA and head office',
      ],
    },
    {
      tag: 'Logistics & Transport',
      tagType: 'cyan',
      title: 'Follow your cold chain from warehouse to delivery',
      description: 'Place sensors in trucks, pallets and mobile coolers to capture every step of the journey.',
      image: '/assets/truck-with-mock-up-space-ads.webp',
      alt: 'Refrigerated truck in transit',
      bullets: [
        'Trip reports that combine GPS, temperature and humidity data',
        'Alerts for door‑open events or unexpected temperature spikes',
        'Share verification reports with customers and auditors via QR codes',
      ],
    },
    {
      tag: 'Fridges & Small Cool Rooms',
      tagType: 'purple',
      title: 'Get visibility into every critical fridge',
      description: 'Whether it’s a home fridge, café display or small cool room, ChillVerify makes sure temperatures stay where they should — even when no one is watching.',
      image: '/assets/composition-healthy-raw-food-fridge.webp',
      alt: 'Open fridge with fresh food',
      bullets: [
        'Drop‑in wireless sensors for household and commercial fridges',
        'SMS and push alerts if doors are left open or compressors fail',
        'Automatic temperature history to support insurance and warranty claims',
      ],
    },
    {
      tag: 'Cheese & Dairy Rooms',
      tagType: 'green',
      title: 'Maintain perfect ageing conditions for cheese',
      description: 'Track temperature and humidity in maturation rooms, cool rooms and display fridges so every wheel stays within the profile your cheesemaker expects.',
      image: '/assets/close-up-tasty-cheese-wheels.webp',
      alt: 'Cheese wheels in a ripening room',
      bullets: [
        'Multi‑sensor coverage for ripening rooms, cold storage and counters',
        'Humidity and temperature alerts when conditions drift from setpoints',
        'Historic trends to fine‑tune recipes and prove quality to inspectors',
      ],
    },
    {
      tag: 'Abattoirs & Large Cold Rooms',
      tagType: 'red',
      title: 'Monitor critical chillers from slaughter floor to cold store',
      description: 'Place sensors throughout carcass chillers and holding rooms so temperature gradients and pull‑down times are always within spec.',
      image: '/assets/fresh-red-meet-butchery-worker-working-background.webp',
      alt: 'Abattoir worker in cold room with hanging meat',
      bullets: [
        'Multi‑level sensing inside large cold rooms and boning areas',
        'Alert rules for slow pull‑down, hot spots or plant failures',
        'Compliance‑ready records for export verification and regulator audits',
      ],
    },
    {
      tag: 'Pharmacies & Clinics',
      tagType: 'cyan',
      title: 'Keep medicines and vaccines within strict temperature bands',
      description: 'Monitor pharmacy fridges and medicine rooms so temperature‑sensitive products always remain within their validated storage ranges.',
      image: '/assets/pharmacist-checking-medicines-drugstore.webp',
      alt: 'Pharmacist checking medicines in a drugstore fridge',
      bullets: [
        '24/7 monitoring for vaccine and medication fridges',
        'Alerts for excursions so stock can be quarantined in time',
        'Audit‑ready logs for regulators and quality management systems',
      ],
    },
  ];


  return (
    <div style={{ backgroundColor: bgPrimary }} data-testid="landing-page">
      <style>{`
        .hero-get-started-btn.cds--btn--tertiary:active,
        .hero-get-started-btn.cds--btn--tertiary:focus {
          color: #ffffff !important;
          border-color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.1) !important;
        }
        .hero-get-started-btn.cds--btn--tertiary:hover {
          color: #ffffff !important;
          border-color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.1) !important;
        }

        /* View Features Button - Darker Blue Background */
        button[data-testid="hero-learn-more"] {
          background-color: #002d9c !important;
        }

        button[data-testid="hero-learn-more"]:hover {
          background-color: #001f6b !important;
        }

        button[data-testid="hero-learn-more"]:active,
        button[data-testid="hero-learn-more"]:focus {
          background-color: #001447 !important;
        }

        /* Auth Sign In Button - Remove white hover */
        button[data-testid="auth-sign-in"]:hover {
          background-color: transparent !important;
          color: inherit !important;
        }

        button[data-testid="auth-sign-in"]:active,
        button[data-testid="auth-sign-in"]:focus {
          background-color: transparent !important;
          color: inherit !important;
        }

        /* Home split CTA — full width below hero */
        .home-split-section {
          display: flex;
          width: 100%;
          min-height: 520px;
          padding: 0;
        }

        .home-split-panel {
          flex: 1 1 50%;
          width: 50%;
          border: none;
          margin: 0;
          padding: 0;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 20px;
          padding: 80px clamp(40px, 7vw, 104px);
          text-align: left;
          color: #ffffff;
          font-family: inherit;
          transition: background-color 0.25s ease, box-shadow 0.25s ease;
          position: relative;
          overflow: hidden;
        }

        .home-split-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 55%);
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
        }

        .home-split-panel:hover::after,
        .home-split-panel:focus-visible::after {
          opacity: 1;
        }

        /* Left: Carbon Cyan 50 — distinct from hero blue, still on-brand */
        .home-split-panel--primary {
          background-color: #0072c3;
        }

        .home-split-panel--primary:hover,
        .home-split-panel--primary:focus-visible {
          background-color: #00539a;
          box-shadow: inset 0 -4px 0 0 rgba(255, 255, 255, 0.22);
        }

        .home-split-panel--primary::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #33b1ff;
          opacity: 0;
          transition: opacity 0.25s ease;
        }

        .home-split-panel--primary:hover::before,
        .home-split-panel--primary:focus-visible::before {
          opacity: 1;
        }

        /* Right: Carbon Teal 50 — related cool-tone, clearly distinct */
        .home-split-panel--secondary {
          background-color: #007d79;
        }

        .home-split-panel--secondary:hover,
        .home-split-panel--secondary:focus-visible {
          background-color: #005d5d;
          box-shadow: inset 0 -4px 0 0 rgba(255, 255, 255, 0.18);
        }

        .home-split-panel--secondary::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #3ddbd9;
          opacity: 0;
          transition: opacity 0.25s ease;
        }

        .home-split-panel--secondary:hover::before,
        .home-split-panel--secondary:focus-visible::before {
          opacity: 1;
        }

        .home-split-panel__icon {
          opacity: 0.95;
          transition: transform 0.25s ease;
        }

        .home-split-panel:hover .home-split-panel__icon,
        .home-split-panel:focus-visible .home-split-panel__icon {
          transform: translateY(-2px);
        }

        .home-split-panel__label {
          font-size: clamp(1.25rem, 2.5vw, 1.75rem);
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
        }

        .home-split-panel__desc {
          font-size: clamp(0.875rem, 1.5vw, 1rem);
          line-height: 1.5;
          opacity: 0.9;
          max-width: 420px;
        }

        .home-split-panel__cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .home-split-panel__arrow {
          transition: transform 0.25s ease;
        }

        .home-split-panel:hover .home-split-panel__arrow,
        .home-split-panel:focus-visible .home-split-panel__arrow {
          transform: translateX(6px);
        }

        .home-split-panel:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: -4px;
        }

        /* Hardware strip — flush below split CTA */
        .hardware-strip-section {
          padding: 0;
        }

        .hardware-strip-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          width: 100%;
          gap: 0;
        }

        .hardware-card {
          border: none;
          margin: 0;
          padding: 0;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          text-align: left;
          font-family: inherit;
          overflow: hidden;
          background: transparent;
          --card-color: #0f62fe;
          --card-hover: #0353e9;
        }

        .hardware-card__header {
          position: relative;
          display: flex;
          align-items: flex-end;
          min-height: 148px;
          padding: 28px 20px;
          color: #ffffff;
          background-color: var(--card-color);
          transition: background-color 0.25s ease, box-shadow 0.25s ease;
          overflow: hidden;
        }

        .hardware-card:hover .hardware-card__header,
        .hardware-card:focus-visible .hardware-card__header {
          background-color: var(--card-hover);
          box-shadow: inset 0 -4px 0 0 rgba(255, 255, 255, 0.15);
        }

        .hardware-card__header::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 55%);
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
        }

        .hardware-card:hover .hardware-card__header::after,
        .hardware-card:focus-visible .hardware-card__header::after {
          opacity: 1;
        }

        .hardware-card__title {
          position: relative;
          z-index: 1;
          font-size: clamp(0.875rem, 1.2vw, 1rem);
          font-weight: 600;
          line-height: 1.3;
          margin: 0;
        }

        .hardware-card__media {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 24px 16px 28px;
          min-height: 240px;
          flex: 1;
          background-color: var(--card-color);
          border: none;
          box-shadow: none;
          overflow: hidden;
          transition: background-color 0.25s ease;
        }

        .hardware-card:hover .hardware-card__media,
        .hardware-card:focus-visible .hardware-card__media,
        .hardware-card--revealed .hardware-card__media {
          background-color: var(--card-hover);
        }

        .hardware-card__media img {
          width: 100%;
          height: 100%;
          max-height: 210px;
          object-fit: contain;
          display: block;
          padding: 0;
          margin: 0;
          background: transparent;
          border: none;
          box-shadow: none;
          transition: transform 0.35s ease, opacity 0.35s ease;
        }

        .hardware-card--revealed .hardware-card__media img {
          transform: scale(0.92);
          opacity: 0.45;
        }

        .hardware-card__reveal {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2;
          padding: 20px 16px 18px;
          color: #ffffff;
          text-align: left;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.88) 0%,
            rgba(0, 0, 0, 0.72) 55%,
            rgba(0, 0, 0, 0) 100%
          );
          transform: translateY(100%);
          opacity: 0;
          transition:
            transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.35s ease;
          pointer-events: none;
        }

        .hardware-card__reveal-type {
          display: block;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          opacity: 0.85;
          margin-bottom: 6px;
        }

        .hardware-card__reveal-desc {
          margin: 0 0 10px;
          font-size: 0.8125rem;
          line-height: 1.45;
          opacity: 0.95;
        }

        .hardware-card__reveal-hint {
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          opacity: 0.7;
        }

        .hardware-card__reveal-actions {
          margin-top: 2px;
        }

        .hardware-card__buy-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 0;
          background: #ffffff;
          color: #161616;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .hardware-card__buy-btn:hover {
          background: #f4f4f4;
          transform: translateX(2px);
        }

        .hardware-card--revealed .hardware-card__reveal {
          transform: translateY(0);
          opacity: 1;
          pointer-events: auto;
        }

        @media (hover: hover) and (pointer: fine) {
          .hardware-card:hover .hardware-card__reveal,
          .hardware-card:focus-within .hardware-card__reveal {
            transform: translateY(0);
            opacity: 1;
            pointer-events: auto;
          }

          .hardware-card:hover .hardware-card__media img {
            transform: scale(0.92);
            opacity: 0.45;
          }

          .hardware-card__reveal-hint {
            display: none;
          }
        }

        .hardware-card--revealed .hardware-card__reveal-hint {
          display: none;
        }

        .hardware-card--revealed:hover .hardware-card__media img {
          transform: scale(0.92);
          opacity: 0.45;
        }

        .hardware-card:focus-visible {
          outline: 2px solid #0f62fe;
          outline-offset: -2px;
        }

        .hardware-byo-card {
          display: block;
          width: 100%;
          border: none;
          margin: 0;
          padding: 40px clamp(24px, 5vw, 72px);
          cursor: pointer;
          text-align: center;
          font-family: inherit;
          background-color: var(--byo-bg, #393939);
          color: var(--byo-text, #ffffff);
          transition: background-color 0.25s ease;
        }

        .hardware-byo-card:hover,
        .hardware-byo-card:focus-visible {
          background-color: var(--byo-bg-hover, #262626);
        }

        .hardware-byo-card:hover .hardware-byo-card__title,
        .hardware-byo-card:focus-visible .hardware-byo-card__title,
        .hardware-byo-card:hover .hardware-byo-card__desc,
        .hardware-byo-card:focus-visible .hardware-byo-card__desc {
          color: var(--byo-text, #ffffff);
        }

        .hardware-byo-card__title {
          color: var(--byo-text, #ffffff);
          font-weight: 600;
          font-size: clamp(1.125rem, 2vw, 1.5rem);
          margin: 0 0 8px;
          transition: color 0.25s ease;
        }

        .hardware-byo-card__desc {
          color: var(--byo-desc, rgba(255, 255, 255, 0.85));
          font-size: clamp(0.875rem, 1.5vw, 1rem);
          margin: 0 auto;
          max-width: 640px;
          line-height: 1.5;
          transition: color 0.25s ease;
        }

        .applications-section {
          padding: 80px 0;
          margin: 0;
        }

        .showcase-section {
          padding: 0;
        }

        .showcase-intro {
          text-align: center;
          padding: 72px clamp(24px, 5vw, 48px) 56px;
          background-color: var(--showcase-intro-bg, #ffffff);
        }

        .showcase-intro--flush {
          padding-top: 56px;
        }

        .showcase-intro__eyebrow {
          margin-bottom: 16px;
        }

        .showcase-intro__title {
          margin: 0;
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 600;
          line-height: 1.15;
          color: var(--showcase-intro-text, #161616);
        }

        .showcase-intro__desc {
          max-width: 760px;
          margin: 16px auto 0;
          font-size: clamp(1rem, 2vw, 1.125rem);
          line-height: 1.7;
          color: var(--showcase-intro-muted, #525252);
        }

        .gps-visibility-pills,
        .showcase-feature-pills {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
          margin-top: 28px;
        }

        .gps-visibility-pill,
        .showcase-feature-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: 1px solid var(--showcase-intro-border, #e0e0e0);
          background: var(--showcase-intro-bg, #ffffff);
          color: var(--showcase-intro-text, #161616);
          font-size: 0.875rem;
          font-weight: 600;
        }

        .showcase-split-row {
          display: flex;
          width: 100%;
          min-height: 480px;
        }

        .showcase-split-panel {
          flex: 1 1 50%;
          width: 50%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 64px clamp(32px, 6vw, 88px);
          color: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .showcase-split-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, transparent 55%);
          pointer-events: none;
        }

        .showcase-split-panel__tag-wrap {
          position: relative;
          z-index: 1;
          margin-bottom: 16px;
        }

        .showcase-split-panel__title {
          margin: 0 0 16px;
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 600;
          line-height: 1.2;
          position: relative;
          z-index: 1;
        }

        .showcase-split-panel__desc {
          margin: 0 0 24px;
          font-size: clamp(0.9375rem, 1.8vw, 1rem);
          line-height: 1.75;
          opacity: 0.92;
          position: relative;
          z-index: 1;
        }

        .showcase-split-panel__bullets {
          list-style: none;
          padding: 0;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        .showcase-split-panel__bullets li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 0.9375rem;
          line-height: 1.6;
          opacity: 0.95;
        }

        .showcase-split-panel__bullets li svg {
          flex-shrink: 0;
          margin-top: 2px;
          color: #6fdc8c;
        }

        .showcase-split-panel--media {
          align-items: center;
          justify-content: center;
          padding: 40px clamp(24px, 4vw, 56px);
        }

        .showcase-split-panel--media img {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 520px;
          max-height: min(420px, 72vh);
          height: auto;
          object-fit: contain;
          border-radius: 8px;
          filter: drop-shadow(0 12px 32px rgba(0, 0, 0, 0.22));
        }


        .showcase-slider {
          position: relative;
        }

        .showcase-slider__viewport {
          overflow: hidden;
        }

        .showcase-slider__container {
          display: flex;
          touch-action: pan-y pinch-zoom;
        }

        .showcase-slider__slide {
          flex: 0 0 100%;
          min-width: 0;
        }

        .showcase-slider__controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          padding: 28px clamp(24px, 5vw, 48px) 48px;
          background-color: var(--showcase-intro-bg, #ffffff);
        }

        .showcase-slider__nav-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border: 1px solid rgba(15, 98, 254, 0.35);
          border-radius: 50%;
          background: transparent;
          color: #0f62fe;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .showcase-slider__nav-btn:hover,
        .showcase-slider__nav-btn:focus-visible {
          background-color: rgba(15, 98, 254, 0.08);
          border-color: #0f62fe;
          outline: none;
        }

        .showcase-slider__dots {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
          max-width: min(640px, 72vw);
        }

        .showcase-slider__dot {
          width: 10px;
          height: 10px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background-color: rgba(15, 98, 254, 0.25);
          cursor: pointer;
          transition: transform 0.2s ease, background-color 0.2s ease;
        }

        .showcase-slider__dot--active {
          background-color: #0f62fe;
          transform: scale(1.15);
        }

        .epcis-traceability-section {
          padding: 0;
        }

        .epcis-traceability-section__intro {
          text-align: center;
          padding: 72px clamp(24px, 5vw, 48px) 56px;
          background-color: var(--epcis-intro-bg, #f4f4f4);
        }

        .epcis-traceability-section__title {
          margin: 16px 0 0;
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 600;
          line-height: 1.15;
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-traceability-section__desc {
          max-width: 820px;
          margin: 16px auto 0;
          font-size: clamp(1rem, 2vw, 1.125rem);
          line-height: 1.75;
          color: var(--epcis-intro-muted, #525252);
        }

        .epcis-traceability-section__block {
          max-width: 820px;
          margin: 36px auto 0;
          text-align: center;
        }

        .epcis-traceability-section__block h3 {
          margin: 0 0 10px;
          font-size: clamp(1.125rem, 2vw, 1.375rem);
          font-weight: 600;
          line-height: 1.3;
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-traceability-section__block p {
          margin: 0;
          font-size: 1rem;
          line-height: 1.7;
          color: var(--epcis-intro-muted, #525252);
        }

        .epcis-power-list {
          list-style: none;
          padding: 0;
          max-width: 820px;
          margin: 16px auto 28px;
        }

        .epcis-power-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
          font-size: 1rem;
          line-height: 1.7;
          color: var(--epcis-intro-muted, #525252);
        }

        .epcis-power-list li:last-child {
          margin-bottom: 0;
        }

        .epcis-power-list li svg {
          flex-shrink: 0;
          margin-top: 3px;
          color: #24a148;
        }

        .epcis-power-list strong {
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-version-compare {
          max-width: 1100px;
          margin: 40px auto 0;
          text-align: left;
        }

        .epcis-version-compare__title {
          margin: 0 0 8px;
          font-size: clamp(1.125rem, 2vw, 1.375rem);
          font-weight: 600;
          line-height: 1.3;
          text-align: center;
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-version-compare__lead {
          max-width: 820px;
          margin: 0 auto 20px;
          font-size: 1rem;
          line-height: 1.7;
          text-align: center;
          color: var(--epcis-intro-muted, #525252);
        }

        .epcis-version-compare__lead strong {
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-version-compare__table-wrap {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border: 1px solid var(--epcis-intro-border, #e0e0e0);
        }

        .epcis-version-compare__table {
          width: 100%;
          min-width: 640px;
          border-collapse: collapse;
          background: var(--epcis-intro-table-bg, #ffffff);
        }

        .epcis-version-compare__table th,
        .epcis-version-compare__table td {
          padding: 12px 16px;
          font-size: 0.875rem;
          line-height: 1.55;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid var(--epcis-intro-border, #e0e0e0);
          color: var(--epcis-intro-title, #161616);
        }

        .epcis-version-compare__table th {
          font-weight: 600;
          background: var(--epcis-intro-bg, #f4f4f4);
          white-space: nowrap;
        }

        .epcis-version-compare__table td:first-child {
          font-weight: 600;
          white-space: nowrap;
        }

        .epcis-version-compare__table th:last-child,
        .epcis-version-compare__table td:last-child {
          text-align: center;
          vertical-align: middle;
          white-space: nowrap;
          width: 7.5rem;
        }

        .epcis-tick {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #24a148;
        }

        .epcis-detail-card__table .epcis-tick {
          color: #42be65;
        }

        .epcis-version-compare__table tr:last-child td {
          border-bottom: none;
        }

        @media (max-width: 672px) {
          .epcis-version-compare__table {
            min-width: 0;
          }
          .epcis-version-compare__table thead {
            display: none;
          }
          .epcis-version-compare__table,
          .epcis-version-compare__table tbody,
          .epcis-version-compare__table tr,
          .epcis-version-compare__table th,
          .epcis-version-compare__table td {
            display: block;
            width: 100%;
            white-space: normal;
          }
          .epcis-version-compare__table tr {
            padding: 12px 14px;
            border-bottom: 1px solid var(--epcis-intro-border, #e0e0e0);
          }
          .epcis-version-compare__table tr:last-child {
            border-bottom: none;
          }
          .epcis-version-compare__table td {
            padding: 4px 0;
            border-bottom: none;
          }
          .epcis-version-compare__table td:first-child {
            padding-top: 0;
            margin-bottom: 4px;
            font-size: 0.9375rem;
          }
          .epcis-version-compare__table td:nth-child(2)::before,
          .epcis-version-compare__table td:nth-child(3)::before,
          .epcis-version-compare__table td:nth-child(4)::before {
            display: block;
            font-weight: 600;
            font-size: 0.75rem;
            letter-spacing: 0.02em;
            text-transform: uppercase;
            color: var(--epcis-intro-muted, #525252);
            margin-bottom: 2px;
          }
          .epcis-version-compare__table td:nth-child(2)::before {
            content: "EPCIS 1.x";
          }
          .epcis-version-compare__table td:nth-child(3)::before {
            content: "EPCIS 2.0";
          }
          .epcis-version-compare__table td:nth-child(4) {
            text-align: left;
            width: auto;
            margin-top: 8px;
          }
          .epcis-version-compare__table td:nth-child(4)::before {
            content: "ChillVerify";
          }
        }

        .epcis-traceability-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          width: 100%;
          gap: 0;
        }

        .epcis-traceability-card {
          border: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          text-align: left;
          overflow: hidden;
          background: transparent;
          --card-color: #0f62fe;
          --card-hover: #0353e9;
          min-height: 520px;
        }

        .epcis-traceability-card__header {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          min-height: 180px;
          padding: 36px 24px;
          color: #ffffff;
          background-color: var(--card-color);
          transition: background-color 0.25s ease, box-shadow 0.25s ease;
          overflow: hidden;
        }

        .epcis-traceability-card:hover .epcis-traceability-card__header {
          background-color: var(--card-hover);
          box-shadow: inset 0 -4px 0 0 rgba(255, 255, 255, 0.15);
        }

        .epcis-traceability-card__header::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 55%);
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
        }

        .epcis-traceability-card:hover .epcis-traceability-card__header::after {
          opacity: 1;
        }

        .epcis-traceability-card__icon {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          margin-bottom: 16px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
        }

        .epcis-traceability-card__event-type {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin-bottom: 8px;
          color: #ffffff;
        }

        .epcis-traceability-card__event-type-line {
          display: block;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          line-height: 1.2;
          opacity: 0.9;
        }

        .epcis-traceability-card__event-type-line--secondary {
          opacity: 0.75;
        }

        .epcis-traceability-card__step {
          position: relative;
          z-index: 1;
          display: block;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          opacity: 0.7;
          margin-bottom: 10px;
          color: #ffffff;
        }

        .epcis-traceability-card__title {
          position: relative;
          z-index: 1;
          margin: 0;
          font-size: clamp(0.875rem, 1.2vw, 1rem);
          font-weight: 600;
          line-height: 1.3;
          color: #ffffff;
        }

        .epcis-traceability-card__body {
          position: relative;
          display: flex;
          align-items: flex-start;
          margin: 0;
          padding: 32px 24px 48px;
          flex: 1;
          background-color: var(--card-color);
          transition: background-color 0.25s ease;
        }

        .epcis-traceability-card:hover .epcis-traceability-card__body {
          background-color: var(--card-hover);
        }

        .epcis-traceability-card__desc {
          margin: 0;
          font-size: 0.8125rem;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.92);
        }

        .epcis-detail-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-top: 32px;
        }

        @media (max-width: 900px) {
          .epcis-detail-cards {
            grid-template-columns: 1fr;
          }
        }

        .epcis-detail-card {
          display: flex;
          flex-direction: column;
          text-align: left;
          overflow: hidden;
          --detail-color: #0f62fe;
          --detail-hover: #0353e9;
          background-color: var(--detail-color);
          height: 100%;
          min-height: 620px;
          transition: background-color 0.25s ease;
        }

        .epcis-detail-card:hover {
          background-color: var(--detail-hover);
        }

        .epcis-detail-card__header {
          padding: 36px 32px 20px;
          color: #ffffff;
        }

        .epcis-detail-card__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          margin-bottom: 16px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
        }

        .epcis-detail-card__title {
          margin: 0 0 8px;
          font-size: clamp(1.1rem, 1.6vw, 1.375rem);
          font-weight: 600;
          line-height: 1.3;
          color: #ffffff;
        }

        .epcis-detail-card__subtitle {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.85);
        }

        .epcis-detail-card__body {
          padding: 8px 32px 32px;
          overflow-x: auto;
          flex: 1;
        }

        .epcis-detail-card__list {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .epcis-detail-card__list li {
          margin-bottom: 12px;
          font-size: 0.875rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.92);
        }

        .epcis-detail-card__list li:last-child {
          margin-bottom: 0;
        }

        .epcis-detail-card__list li strong {
          color: #ffffff;
        }

        .epcis-detail-card__table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.75rem;
        }

        .epcis-detail-card__table thead {
          display: none;
        }

        .epcis-detail-card__table,
        .epcis-detail-card__table tbody,
        .epcis-detail-card__table tr,
        .epcis-detail-card__table th,
        .epcis-detail-card__table td {
          display: block;
          width: 100%;
        }

        .epcis-detail-card__table tr {
          position: relative;
          padding: 12px 40px 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.18);
        }

        .epcis-detail-card__table tr:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .epcis-detail-card__table td {
          padding: 0 0 4px;
          color: rgba(255, 255, 255, 0.92);
          border-bottom: none;
          white-space: normal;
          text-align: left;
          width: 100%;
        }

        .epcis-detail-card__table td:first-child {
          font-weight: 600;
          font-size: 0.875rem;
          color: #ffffff;
          padding-bottom: 6px;
        }

        .epcis-detail-card__table td:nth-child(2)::before,
        .epcis-detail-card__table td:nth-child(3)::before {
          display: block;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1px;
        }

        .epcis-detail-card__table td:nth-child(2)::before {
          content: "Question";
        }

        .epcis-detail-card__table td:nth-child(3)::before {
          content: "GS1 encoding";
        }

        .epcis-detail-card__table td:last-child {
          position: absolute;
          top: 12px;
          right: 0;
          width: auto;
          padding: 0;
        }

        .epcis-detail-card__table tr:last-child td {
          border-bottom: none;
        }



        @media (max-width: 1056px) {
          .epcis-traceability-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 672px) {
          .epcis-traceability-grid {
            grid-template-columns: 1fr !important;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {

          .epcis-traceability-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }

          .showcase-slider__controls {
            padding: 20px 16px 32px !important;
          }

          .showcase-slider__dots {
            max-width: 100% !important;
          }

          /* Hero Section */
          section[data-testid="hero-section"] {
            padding: 40px 0 !important;
          }

          section[data-testid="hero-section"] h1 {
            font-size: 2.5rem !important;
            line-height: 1.1 !important;
            margin-bottom: 16px !important;
          }

          section[data-testid="hero-section"] p {
            font-size: 1rem !important;
            margin-bottom: 24px !important;
          }

          section[data-testid="hero-section"] img {
            max-width: 80% !important;
            margin-top: 32px;
          }

          .home-split-section {
            flex-direction: column;
            min-height: 0;
          }

          .home-split-panel {
            width: 100%;
            flex: none;
            padding: 48px 28px;
            min-height: 320px;
          }

          /* Show mobile download buttons, hide desktop */
          .desktop-download-buttons {
            display: none !important;
          }
          .mobile-download-buttons {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
            gap: 10px !important;
            max-width: 100% !important;
          }
          .mobile-download-buttons button {
            flex: 0 0 auto !important;
            max-width: 160px !important;
          }
          .mobile-download-buttons button img {
            height: 40px !important;
            width: auto !important;
            max-width: 100% !important;
            object-fit: contain !important;
            display: block !important;
          }

          /* All Sections Padding */
          section:not([data-testid="home-split-section"]):not([data-testid="epcis-traceability-section"]):not([data-testid="shop-preview-section"]) {
            padding: 40px 0 !important;
          }

          section[data-testid="home-split-section"],
          section[data-testid="epcis-traceability-section"],
          section[data-testid="shop-preview-section"] {
            padding: 0 !important;
          }

          /* Section Headings */
          section h2 {
            font-size: 1.75rem !important;
            margin-bottom: 32px !important;
          }

          section h3 {
            font-size: 1.125rem !important;
          }

          section h4 {
            font-size: 1rem !important;
          }

          /* Core Features Grid - 3 columns to 1 */
          section[data-testid="core-features-section"] > div > div {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }

          /* Hardware strip — 2 columns on mobile */
          .hardware-strip-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          .hardware-card__header {
            min-height: 112px !important;
            padding: 22px 16px !important;
          }

          .hardware-card__media {
            min-height: 175px !important;
            padding: 18px 12px 22px !important;
          }

          .hardware-card__media img {
            max-height: 150px !important;
            height: auto !important;
          }

          .showcase-split-row {
            flex-direction: column;
            min-height: unset;
          }

          .showcase-split-panel {
            width: 100%;
            flex: none;
            padding: 40px 24px !important;
          }

          .showcase-split-panel--media {
            order: -1;
            min-height: 280px;
            padding: 32px 20px !important;
          }

          .showcase-split-panel--media img {
            max-height: 240px !important;
          }

          .showcase-intro {
            padding: 48px 20px 40px !important;
          }

          .showcase-intro__title {
            font-size: 2rem !important;
          }

          /* Alert Features Grid - 2 columns to 1 */
          section[data-testid="alerts-section"] .grid-2 {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }

          /* Alert Images */
          section[data-testid="alerts-section"] img {
            max-width: 100% !important;
            margin-bottom: 24px;
          }

          /* Tile Padding */
          .feature-card {
            padding: 20px !important;
          }

          /* Platform Icons - Keep horizontal on mobile - MUST COME FIRST */
          [data-testid="platform-icons-container"] {
            flex-wrap: wrap !important;
            flex-direction: row !important;
          }

          [data-testid="platform-icons"] {
            flex-wrap: nowrap !important;
            flex-direction: row !important;
            white-space: nowrap !important;
            display: flex !important;
          }

          [data-testid="platform-icons"] svg {
            flex-shrink: 0 !important;
          }

          /* Button Groups - Exclude platform icons */
          section div[style*="display: flex"][style*="gap: 16px"]:not([data-testid="platform-icons"]):not([data-testid="platform-icons-container"]) {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          section div[style*="display: flex"][style*="gap: 16px"]:not([data-testid="platform-icons"]):not([data-testid="platform-icons-container"]) button {
            width: 100% !important;
          }

          /* Max Width Containers */
          section > div[style*="maxWidth"] {
            padding: 0 16px !important;
          }

          /* Applications Section */
          section[data-testid="applications-section"] [class*="cds--col"] {
            max-width: 100% !important;
            flex: 0 0 100% !important;
            margin-bottom: 16px;
          }

          /* Benefits Section */
          section[data-testid="benefits-section"] [class*="cds--col"] {
            max-width: 100% !important;
            flex: 0 0 100% !important;
            margin-bottom: 16px;
          }

          /* Carbon Grid Adjustments for Mobile */
          .cds--grid {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          /* Ensure images don't overflow */
          img {
            max-width: 100% !important;
            height: auto !important;
          }

          /* BYO card — full width */
          section[data-testid="shop-preview-section"] .hardware-byo-card {
            padding: 28px 20px !important;
          }

          section[data-testid="shop-preview-section"] .hardware-byo-card__title {
            font-size: 1.125rem !important;
          }

          section[data-testid="shop-preview-section"] .hardware-byo-card__desc {
            font-size: 0.875rem !important;
          }

          /* About Section List */
          section[data-testid="about-section"] ul {
            padding-left: 0 !important;
          }

          /* QR Verification Section - Stack columns on mobile */
          section[data-testid="qr-verification-section"] [class*="cds--col"] {
            max-width: 100% !important;
            flex: 0 0 100% !important;
            margin-bottom: 32px;
          }

          section[data-testid="qr-verification-section"] [class*="cds--col"]:last-child {
            margin-bottom: 0;
          }

          section[data-testid="qr-verification-section"] [class*="cds--col"] > div[style*="paddingLeft"],
          section[data-testid="qr-verification-section"] [class*="cds--col"] > div[style*="paddingRight"] {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          section[data-testid="qr-verification-section"] img {
            margin-bottom: 24px;
            max-width: 80% !important;
          }
        }

        /* Extra Small Mobile Devices */
        @media (max-width: 480px) {
          /* Hero Section */
          section[data-testid="hero-section"] h1 {
            font-size: 2rem !important;
            line-height: 1.1 !important;
          }

          section[data-testid="hero-section"] p {
            font-size: 0.9375rem !important;
          }

          section[data-testid="hero-section"] img {
            max-width: 100% !important;
          }

          /* Make download buttons smaller on very small screens, keep both consistent */
          .mobile-download-buttons button {
            max-width: 140px !important;
          }
          .mobile-download-buttons button img {
            height: 36px !important;
            width: auto !important;
            max-width: 100% !important;
            object-fit: contain !important;
          }

          /* Further reduce gap on very small screens */
          section[data-testid="hero-section"] div[style*="display: flex"][style*="gap: 12px"] {
            gap: 6px !important;
          }

          /* Platform Icons - Ensure they stay horizontal even on tiny screens */
          [data-testid="platform-icons"] {
            gap: 12px !important;
            flex-direction: row !important;
            display: flex !important;
          }

          [data-testid="platform-icons"] svg {
            width: 20px !important;
            height: 20px !important;
          }

          [data-testid="platform-icons-container"] {
            flex-direction: row !important;
          }

          [data-testid="platform-icons-container"] span {
            font-size: 0.75rem !important;
          }

          /* Section Headings */
          section h2 {
            font-size: 1.5rem !important;
          }

          /* Hardware strip — 1 column on very small screens */
          .hardware-strip-grid {
            grid-template-columns: 1fr !important;
          }

          /* Card Icons */
          .feature-card > div[style*="borderRadius: 50%"] {
            width: 48px !important;
            height: 48px !important;
          }

          .feature-card > div[style*="borderRadius: 50%"] svg {
            width: 24px !important;
            height: 24px !important;
          }

          /* Reduce all section padding further */
          section:not([data-testid="home-split-section"]):not([data-testid="shop-preview-section"]) {
            padding: 32px 0 !important;
          }

          section[data-testid="shop-preview-section"] {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
        }

        /* Tablet Adjustments */
        @media (min-width: 769px) and (max-width: 1024px) {
          /* Core Features Grid - 3 columns to 2 */
          section[data-testid="core-features-section"] > div > div {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          /* Hardware strip — 3 columns on tablet */
          .hardware-strip-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }

          /* QR Verification Section - Reduce padding on tablet */
          section[data-testid="qr-verification-section"] [class*="cds--col"] > div[style*="paddingLeft"],
          section[data-testid="qr-verification-section"] [class*="cds--col"] > div[style*="paddingRight"] {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
        }
      `}</style>
      {/* Hero Section */}
      <section
        style={{
          backgroundColor: '#0f62fe',
          padding: '80px 0',
          position: 'relative',
          overflow: 'hidden',
        }}
        data-testid="hero-section"
      >
        <Grid>
          <Column lg={8} md={6} sm={4}>
            <div style={{ color: '#ffffff' }} className="animate-fade-in">
              <Tag type="high-contrast" style={{ marginBottom: '16px' }}>IoT Monitoring Platform</Tag>
              <h1 className="app-heading-hero" style={{ marginBottom: '24px' }}>
                Verify Temperature & Humidity with Ease
              </h1>
              <p style={{ fontSize: '1.25rem', marginBottom: '32px', opacity: 0.9, maxWidth: '500px' }}>
                Advanced IoT telematics and sensor solutions delivering integrated real-time analytics, environmental monitoring, traceability, and verifiable data integrity.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Button 
                  kind="tertiary" 
                  size="lg" 
                  onClick={() => navigate('/signup')} 
                  data-testid="hero-get-started"
                  style={{ 
                    color: '#ffffff', 
                    borderColor: '#ffffff',
                    backgroundColor: 'transparent',
                  }}
                  className="hero-get-started-btn"
                >
                  Get Started
                </Button>
                <Button
                  kind="ghost"
                  size="lg"
                  onClick={() => navigate('/features')}
                  data-testid="hero-learn-more"
                  style={{ color: '#ffffff', backgroundColor: '#002d9c' }}
                >
                  View Features <ArrowRight style={{ marginLeft: '8px', fill: '#ffffff' }} />
                </Button>
              </div>
              
              {/* Download Buttons - Desktop only */}
              <div className="desktop-download-buttons" style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
                <button
                  onClick={() => window.open('https://apps.apple.com/au/app/chill-verify/id6749800811', '_blank')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    borderRadius: '0',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  aria-label="Download iOS App"
                  data-testid="hero-ios-download"
                >
                  <img 
                    src={appleBadge} 
                    alt="Download on App Store" 
                    style={{ 
                      height: '48px', 
                      width: 'auto', 
                      display: 'block'
                    }}
                  />
                </button>
                
                <button
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mazinoamuno.sensorpushapp&hl=en', '_blank')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    borderRadius: '0',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  aria-label="Download Android App"
                  data-testid="hero-android-download"
                >
                  <img 
                    src={googlePlayBadge} 
                    alt="Get it on Google Play" 
                    style={{ 
                      height: '48px', 
                      width: 'auto', 
                      display: 'block'
                    }}
                  />
                </button>
              </div>

              {/* Download Buttons - Mobile only: sizes controlled by CSS for consistency */}
              <div className="mobile-download-buttons" style={{ display: 'none', flexDirection: 'row', gap: '12px', marginTop: '24px', alignItems: 'center', justifyContent: 'flex-start' }}>
                <button
                  onClick={() => window.open('https://apps.apple.com/au/app/chill-verify/id6749800811', '_blank')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    borderRadius: '0',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  aria-label="Download iOS App"
                  data-testid="hero-ios-download-mobile"
                >
                  <img 
                    src={appleBadge} 
                    alt="Download on App Store" 
                    style={{ height: '40px', width: 'auto', display: 'block', objectFit: 'contain' }}
                  />
                </button>
                <button
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mazinoamuno.sensorpushapp&hl=en', '_blank')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    borderRadius: '0',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  aria-label="Download Android App"
                  data-testid="hero-android-download-mobile"
                >
                  <img 
                    src={googlePlayBadge} 
                    alt="Get it on Google Play" 
                    style={{ height: '40px', width: 'auto', display: 'block', objectFit: 'contain' }}
                  />
                </button>
              </div>
              {/* Platform Icons */}
              <div style={{ display: 'flex', gap: '24px', marginTop: '32px', alignItems: 'center', flexWrap: 'wrap' }} data-testid="platform-icons-container">
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>Available on:</span>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'nowrap' }} data-testid="platform-icons">
                  {/* iOS Icon */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#ffffff"/>
                  </svg>
                  {/* Android Icon */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.523 15.3414c-.5511 0-.9993-.4482-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5515-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4482-.9993-.9997s.4482-.9993.9993-.9993c.551 0 .9993.4482.9993.9993 0 .5515-.4483.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1349 1.2297L4.8428 5.5773a.4161.4161 0 00-.5676-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.186 1 13.9524 1 17.3054c0 .6448.5222 1.167 1.167 1.167h21.666c.6448 0 1.167-.5222 1.167-1.167 0-3.353-1.6889-6.1194-4.2855-7.984z" fill="#ffffff"/>
                  </svg>
                  {/* Web Icon */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="#ffffff"/>
                  </svg>
                </div>
              </div>
            </div>
          </Column>
          <Column lg={8} md={6} sm={4}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <img
                src="/assets/SensorsSummary.webp"
                alt="ChillVerify Dashboard"
                style={{ maxWidth: '50%' }}
                data-testid="hero-image"
              />
            </div>
          </Column>
        </Grid>
      </section>

<section
        className="hardware-strip-section"
        data-testid="shop-preview-section"
        aria-label="Sensors and integrations"
        style={{
          '--showcase-intro-bg': bgPrimary,
          '--showcase-intro-text': textPrimary,
          '--showcase-intro-muted': textSecondary,
        }}
      >
        <div className="showcase-intro showcase-intro--flush">
          <div className="showcase-intro__eyebrow">
            <Tag type="magenta">Hardware</Tag>
          </div>
          <h2 className="showcase-intro__title">Shop Sensors, Gateways or Bring Your Own</h2>
        </div>
        <div className="hardware-strip-grid">
          {sensors.map((sensor) => {
            const isRevealed = revealedSensor === sensor.name;
            const shopUrl = sensorShopUrls[sensor.shopMatch] || '/shop';
            return (
            <div
              key={sensor.name}
              role="button"
              tabIndex={0}
              className={`hardware-card${isRevealed ? ' hardware-card--revealed' : ''}`}
              style={{ '--card-color': sensor.color, '--card-hover': sensor.hoverColor }}
              onClick={(e) => handleSensorCardClick(sensor, shopUrl, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSensorCardClick(sensor, shopUrl, e);
                }
              }}
              aria-expanded={isRevealed}
              aria-label={`${sensor.name}: ${sensor.type}. Tap for details.`}
              data-testid={`sensor-${sensor.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="hardware-card__header">
                <h4 className="hardware-card__title">{sensor.name}</h4>
              </div>
              <div className="hardware-card__media">
                <img src={sensor.image} alt={sensor.name} loading="lazy" />
                <div className="hardware-card__reveal">
                  <span className="hardware-card__reveal-type">{sensor.type}</span>
                  <p className="hardware-card__reveal-desc">{sensor.description}</p>
                  <div className="hardware-card__reveal-actions">
                    <button
                      type="button"
                      className="hardware-card__buy-btn"
                      onClick={(e) => handleSensorBuyNow(shopUrl, e)}
                      data-testid={`buy-${sensor.shopMatch}`}
                    >
                      Buy Now
                      <ArrowRight size={14} />
                    </button>
                  </div>
                  <span className="hardware-card__reveal-hint">Tap again to shop</span>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <button
          type="button"
          className="hardware-byo-card"
          style={{
            '--byo-bg': byoBg,
            '--byo-bg-hover': byoBgHover,
            '--byo-text': byoText,
            '--byo-desc': byoDesc,
          }}
          onClick={() => navigate('/contact')}
          data-testid="byo-sensor"
        >
          <h4 className="hardware-byo-card__title">Bring Your Own (BYO)</h4>
          <p className="hardware-byo-card__desc">
            Tell us your brand and we'll customize integration - at no cost
          </p>
        </button>
      </section>

<section
        className="showcase-section"
        data-testid="app-screenshots-section"
        style={{
          '--showcase-intro-bg': bgPrimary,
          '--showcase-intro-text': textPrimary,
          '--showcase-intro-muted': textSecondary,
        }}
      >
        <div className="showcase-intro">
          <div className="showcase-intro__eyebrow">
            <Tag type="cyan">Product Screenshots</Tag>
          </div>
          <h2 className="showcase-intro__title">See ChillVerify In Action</h2>
          <p className="showcase-intro__desc">
            Explore the main app workflows with screenshots and clear descriptions of how each feature helps you monitor, report and verify cold chain conditions.
          </p>
        </div>

        <LandingShowcaseSlider rows={appShowcaseRows} testIdPrefix="showcase" />
      </section>

<section
        className="showcase-section"
        data-testid="applications-split-section"
        style={{
          '--showcase-intro-bg': bgPrimary,
          '--showcase-intro-text': textPrimary,
          '--showcase-intro-muted': textSecondary,
        }}
      >
        <div className="showcase-intro showcase-intro--flush">
          <div className="showcase-intro__eyebrow">
            <Tag type="teal">Applications</Tag>
          </div>
          <h2 className="showcase-intro__title">Environmental Monitoring Applications</h2>
          <p className="showcase-intro__desc">
            See how sensors, gateways and dashboards come together across different industries to protect stock,
            stay compliant and give teams peace of mind.
          </p>
        </div>

        <LandingShowcaseSlider
          rows={industryShowcaseRows}
          colorOffset={appShowcaseRows.length}
          mediaFirstWhenEven
          testIdPrefix="industry"
        />
      </section>

<section
        className="showcase-section"
        data-testid="gps-tracking-benefit-section"
        style={{
          '--showcase-intro-bg': bgSecondary,
          '--showcase-intro-text': textPrimary,
          '--showcase-intro-muted': textSecondary,
          '--showcase-intro-border': borderColor,
        }}
      >
        <div className="showcase-intro showcase-intro--flush">
          <div className="showcase-intro__eyebrow">
            <Tag type="cyan">Shipment visibility</Tag>
          </div>
          <h2 className="showcase-intro__title">Live GPS Tracking</h2>
          <p className="showcase-intro__desc">
            Live GPS tracking including temperature and humidity monitoring in real time, so you never lose
            visibility of your shipments — anywhere, any day, any time.
          </p>
          <div className="gps-visibility-pills">
            <span className="gps-visibility-pill">
              <Delivery size={18} />
              Live GPS
            </span>
            <span className="gps-visibility-pill">
              <Temperature size={18} />
              Temperature
            </span>
            <span className="gps-visibility-pill">
              <Humidity size={18} />
              Humidity
            </span>
          </div>
        </div>
        {gpsTrackingRows.map((row, index) => {
          const mediaFirst = index % 2 === 1;
          const textPanel = (
            <div
              className="showcase-split-panel"
              style={{ backgroundColor: row.color }}
              data-testid={`gps-text-${index}`}
            >
              <div className="showcase-split-panel__tag-wrap">
                <Tag type={row.tagType}>{row.tag}</Tag>
              </div>
              <h3 className="showcase-split-panel__title">{row.title}</h3>
              <p className="showcase-split-panel__desc">{row.description}</p>
              <ul className="showcase-split-panel__bullets">
                {row.bullets.map((bullet) => (
                  <li key={bullet}>
                    <CheckmarkFilled size={20} />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
          const mediaPanel = (
            <div
              className="showcase-split-panel showcase-split-panel--media"
              style={{ backgroundColor: row.hoverColor }}
              data-testid={`gps-media-${index}`}
            >
              <img src={row.image} alt={row.alt} loading="lazy" />
            </div>
          );
          return (
            <div className="showcase-split-row" key={row.title} data-testid={`gps-row-${index}`}>
              {mediaFirst ? (
                <>
                  {mediaPanel}
                  {textPanel}
                </>
              ) : (
                <>
                  {textPanel}
                  {mediaPanel}
                </>
              )}
            </div>
          );
        })}
      </section>

<section
        className="showcase-section"
        data-testid="gs1-qr-handoff-section"
        style={{
          '--showcase-intro-bg': bgPrimary,
          '--showcase-intro-text': textPrimary,
          '--showcase-intro-muted': textSecondary,
          '--showcase-intro-border': borderColor,
        }}
      >
        <div className="showcase-intro showcase-intro--flush">
          <div className="showcase-intro__eyebrow">
            <Tag type="teal">GS1 labels</Tag>
          </div>
          <h2 className="showcase-intro__title">GS1 QR Handoff &amp; Label Printing</h2>
          <p className="showcase-intro__desc">
            Scan a GS1 QR code to hand off custody, then print QR labels on the fly and paste them onto pallets.
            ChillVerify – Brother Bluetooth connectivity built in — no extra dongle or cable.
          </p>
          <div className="showcase-feature-pills">
            <span className="showcase-feature-pill">
              <QrCode size={18} />
              GS1 QR Handoff
            </span>
            <span className="showcase-feature-pill">
              <Printer size={18} />
              Print on the fly
            </span>
            <span className="showcase-feature-pill">
              <MobileCheck size={18} />
              Bluetooth
            </span>
          </div>
        </div>
        {gs1QrRows.map((row, index) => {
          const mediaFirst = index % 2 === 1;
          const textPanel = (
            <div
              className="showcase-split-panel"
              style={{ backgroundColor: row.color }}
              data-testid={`gs1-text-${index}`}
            >
              <div className="showcase-split-panel__tag-wrap">
                <Tag type={row.tagType}>{row.tag}</Tag>
              </div>
              <h3 className="showcase-split-panel__title">{row.title}</h3>
              <p className="showcase-split-panel__desc">{row.description}</p>
              <ul className="showcase-split-panel__bullets">
                {row.bullets.map((bullet) => (
                  <li key={bullet}>
                    <CheckmarkFilled size={20} />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
          const mediaPanel = (
            <div
              className="showcase-split-panel showcase-split-panel--media"
              style={{ backgroundColor: row.hoverColor }}
              data-testid={`gs1-media-${index}`}
            >
              <img src={row.image} alt={row.alt} loading="lazy" />
            </div>
          );
          return (
            <div className="showcase-split-row" key={row.title} data-testid={`gs1-row-${index}`}>
              {mediaFirst ? (
                <>
                  {mediaPanel}
                  {textPanel}
                </>
              ) : (
                <>
                  {textPanel}
                  {mediaPanel}
                </>
              )}
            </div>
          );
        })}
      </section>

<section
        className="epcis-traceability-section"
        style={{
          '--epcis-intro-bg': bgSecondary,
          '--epcis-intro-title': textPrimary,
          '--epcis-intro-muted': textSecondary,
          '--epcis-intro-border': borderColor,
          '--epcis-intro-table-bg': bgPrimary,
        }}
        data-testid="epcis-traceability-section"
      >
        <div className="epcis-traceability-section__intro">
          <Tag type="blue">EPCIS 2.0</Tag>
          <h2 className="epcis-traceability-section__title">
            ChillVerify Delivers EPCIS-Compliant Traceability
          </h2>
          <p className="epcis-traceability-section__desc">
            ChillVerify provides end-to-end, EPCIS-compliant traceability across cold chains and supply networks.
            By aligning with global standards, ChillVerify turns fragmented data into an auditable, real-time
            record of product movement and environmental conditions.
          </p>

          <div className="epcis-traceability-section__block">
            <h3>What is EPCIS Compliance?</h3>
            <p>
              EPCIS (Electronic Product Code Information Services) is the GS1 global standard designed to
              capture, store, and share visibility data across supply chain partners. It transforms isolated
              supply chain events — such as scans, handoffs, and location updates — into a unified,
              vendor-neutral event history that tracks physical assets across global trade networks.
            </p>
          </div>

          <div className="epcis-version-compare" data-testid="epcis-version-compare">
            <h3 className="epcis-version-compare__title">The Power of EPCIS 2.0</h3>
            <p className="epcis-version-compare__lead">
              While legacy EPCIS versions (1.0–1.2) recorded the basic <strong>what, when, where, and why</strong> of an asset
              using XML, environmental conditions like temperature were stored in separate, disconnected logs.
              EPCIS 2.0 modernizes supply chain tracking by:
            </p>
            <ul className="epcis-power-list">
              <li>
                <CheckmarkFilled size={20} />
                <span>
                  <strong>Native sensor data integration:</strong> Embedding real-time temperature, humidity,
                  and condition metrics directly within individual tracking events.
                </span>
              </li>
              <li>
                <CheckmarkFilled size={20} />
                <span>
                  <strong>Modern web standards:</strong> Utilizing JSON-LD and REST APIs for seamless,
                  developer-friendly system integration.
                </span>
              </li>
              <li>
                <CheckmarkFilled size={20} />
                <span>
                  <strong>Real-time auditing:</strong> Combining location handoffs and environmental monitoring
                  into a single, tamper-evident data stream.
                </span>
              </li>
            </ul>
            <div className="epcis-version-compare__table-wrap">
              <table className="epcis-version-compare__table">
                <thead>
                  <tr>
                    <th>Capability</th>
                    <th>EPCIS 1.x</th>
                    <th>EPCIS 2.0</th>
                    <th>ChillVerify</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { cap: 'Encoding', prev: 'XML documents', next: 'JSON / JSON-LD (XML still supported), with a GS1 Linked Data context' },
                    { cap: 'Sharing', prev: 'SOAP capture and query services', next: 'REST + OpenAPI — partners exchange events with ordinary HTTP' },
                    { cap: 'Condition / IoT', prev: 'Outside the event, in proprietary logs', next: 'sensorElementList on the event: temperature, humidity, shock, device ID' },
                    { cap: 'Location', prev: 'Business locations (GLN)', next: 'GLN plus GeoJSON coordinates and areas' },
                    { cap: 'Identifiers', prev: 'EPC URNs (SGTIN, SSCC, GIAI)', next: 'URNs plus GS1 Digital Link HTTP URIs for QR-linked reports' },
                    { cap: 'Certification', prev: 'Out of band', next: 'certificationInfo attached to the visibility record' },
                  ].map((row) => (
                    <tr key={row.cap}>
                      <td>{row.cap}</td>
                      <td>{row.prev}</td>
                      <td>{row.next}</td>
                      <td>
                        <span className="epcis-tick" aria-label="Supported by ChillVerify">
                          <CheckmarkFilled size={20} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="epcis-detail-cards">
          <div
            className="epcis-detail-card"
            style={{ '--detail-color': '#0f62fe', '--detail-hover': '#0353e9' }}
            data-testid="epcis-core-architecture-card"
          >
            <div className="epcis-detail-card__header">
              <div className="epcis-detail-card__icon" aria-hidden="true">
                <Analytics size={24} />
              </div>
              <h3 className="epcis-detail-card__title">1. The Core Architecture: The &ldquo;5 Ws&rdquo;</h3>
              <p className="epcis-detail-card__subtitle">
                Every EPCIS event answers five core questions to establish an auditable record.
              </p>
            </div>
            <div className="epcis-detail-card__body">
              <table className="epcis-detail-card__table" data-testid="epcis-5ws-table">
                <thead>
                  <tr>
                    {['Dimension', 'Question', 'Standard GS1 Encoding Example', 'ChillVerify'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { dim: 'What', q: 'Which products, batches, or pallets were involved?', ex: 'SGTIN (Serialized Global Trade Item Number), SSCC (Serial Shipping Container Code)' },
                    { dim: 'When', q: 'At what exact time did this occur?', ex: 'ISO 8601 Timestamp (2026-09-23T18:18:23Z)' },
                    { dim: 'Where', q: 'At what physical location or read point did it happen?', ex: 'GLN (Global Location Number) or Geolocation (geo:lat,long)' },
                    { dim: 'Why', q: 'What business process or step was taking place?', ex: 'CBV (Core Business Vocabulary): commissioning, shipping, receiving, in_transit' },
                    { dim: 'How / Condition', q: 'What was the environmental state during the event?', ex: 'Sensor Element List: Temperature (CEL), Humidity (%RH), Shock, Tilt' },
                  ].map((row) => (
                    <tr key={row.dim} data-testid={`epcis-5ws-row-${row.dim.toLowerCase().replace(/[\s/]+/g, '-')}`}>
                      <td style={{ fontWeight: 600 }}>{row.dim}</td>
                      <td>{row.q}</td>
                      <td>{row.ex}</td>
                      <td>
                        <span className="epcis-tick" aria-label="Supported by ChillVerify">
                          <CheckmarkFilled size={20} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div
            className="epcis-detail-card"
            style={{ '--detail-color': '#6929c4', '--detail-hover': '#491d8b' }}
            data-testid="epcis-event-types-card"
          >
            <div className="epcis-detail-card__header">
              <div className="epcis-detail-card__icon" aria-hidden="true">
                <TreeView size={24} />
              </div>
              <h3 className="epcis-detail-card__title">2. Event Types</h3>
              <p className="epcis-detail-card__subtitle">
                EPCIS structures data into distinct event classes depending on the operation.
              </p>
            </div>
            <div className="epcis-detail-card__body">
              <ul className="epcis-detail-card__list">
                <li>
                  <strong>ObjectEvent:</strong> Tracks the observation or change of state of specific physical
                  items (e.g. a pallet of vaccines is scanned upon arrival at a distribution center).
                </li>
                <li>
                  <strong>AggregationEvent:</strong> Tracks parent-child relationships when items are packed or
                  unpacked (e.g. 100 individual cartons [GTINs] are packed onto 1 wooden pallet [SSCC]).
                </li>
                <li>
                  <strong>TransformationEvent:</strong> Tracks irreversible changes where inputs become new
                  outputs (e.g. raw ingredients processed into a finished pharmaceutical batch).
                </li>
                <li>
                  <strong>AssociationEvent:</strong> Links physical objects to sensors, physical assets, or
                  persistent transport equipment (e.g. attaching an IoT thermal logger [GIAI] to a specific
                  refrigerated shipping container).
                </li>
              </ul>
            </div>
          </div>

          <div
            className="epcis-detail-card"
            style={{ '--detail-color': '#24a148', '--detail-hover': '#198038' }}
            data-testid="epcis-benefits-card"
          >
            <div className="epcis-detail-card__header">
              <div className="epcis-detail-card__icon" aria-hidden="true">
                <Certificate size={24} />
              </div>
              <h3 className="epcis-detail-card__title">Key Business Benefits &amp; Compliance Drivers</h3>
            </div>
            <div className="epcis-detail-card__body">
              <ul className="epcis-detail-card__list">
                <li>
                  <strong>End-to-End Cold Chain Integrity:</strong> Integrates live environmental telemetry (e.g.
                  ChillVerify) directly into custody transfers to prove regulatory compliance — such as FDA FSMA
                  204 or DSCSA — without relying on disconnected third-party logs.
                </li>
                <li>
                  <strong>Interoperability Across Platforms:</strong> Allows suppliers, logistics providers,
                  enterprise ERPs (SAP, Oracle), and regulatory databases to exchange tracking data without custom
                  data connectors.
                </li>
                <li>
                  <strong>Targeted Recall Management:</strong> Enables precise, surgical product recalls down to
                  individual lot numbers or specific time-temperature excursion windows rather than sweeping
                  inventory purges.
                </li>
                <li>
                  <strong>Anti-Counterfeiting &amp; Provenance:</strong> Provides verifiable proof of origin and
                  continuous chain of custody for high-value pharma, food, luxury goods, and industrial assets.
                </li>
              </ul>
            </div>
          </div>
          </div>
        </div>
        <div className="epcis-traceability-grid">
          {epcisTraceabilitySteps.map((item) => {
            const StepIcon = item.icon;
            const [eventTypePrimary, eventTypeSecondary] = splitEpcisEventType(item.eventType);
            return (
            <div
              key={item.step}
              className="epcis-traceability-card"
              style={{ '--card-color': item.color, '--card-hover': item.hoverColor }}
              data-testid={`epcis-step-${item.step}`}
            >
              <div className="epcis-traceability-card__header">
                <div className="epcis-traceability-card__icon" aria-hidden="true">
                  <StepIcon size={24} />
                </div>
                <span className="epcis-traceability-card__event-type">
                  <span className="epcis-traceability-card__event-type-line">{eventTypePrimary}</span>
                  {eventTypeSecondary ? (
                    <span className="epcis-traceability-card__event-type-line epcis-traceability-card__event-type-line--secondary">
                      {eventTypeSecondary}
                    </span>
                  ) : null}
                </span>
                <span className="epcis-traceability-card__step">Step {item.step}</span>
                <h3 className="epcis-traceability-card__title">{item.title}</h3>
              </div>
              <div className="epcis-traceability-card__body">
                <p className="epcis-traceability-card__desc">{item.description}</p>
              </div>
            </div>
            );
          })}
        </div>
      </section>

<section
        className="applications-section"
        style={{ backgroundColor: '#0f62fe' }}
        data-testid="applications-section"
      >
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="high-contrast" style={{ marginBottom: '8px' }}>Applications</Tag>
              <h2 style={{ color: '#ffffff', marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                Use Cases
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '600px', margin: '16px auto 0' }}>
                ChillVerify is trusted across industries to ensure optimal conditions and compliance
              </p>
            </div>
          </Column>
          {[
            {
              title: 'Butchers',
              description: 'Monitor meat storage temperatures to ensure food safety compliance and prevent spoilage. Maintain optimal conditions for fresh and frozen products.',
              icon: Temperature,
            },
            {
              title: 'Cold Chain Logistics',
              description: 'Track temperature and humidity throughout the entire supply chain. Ensure products maintain required conditions from warehouse to delivery.',
              icon: LocationCompany,
            },
            {
              title: 'Pharmaceuticals',
              description: 'Critical monitoring for medication storage. Ensure compliance with strict temperature requirements for vaccines, insulin, and other temperature-sensitive drugs.',
              icon: Security,
            },
            {
              title: 'Office Buildings',
              description: 'Monitor HVAC systems and environmental conditions to ensure comfortable workspaces and optimize energy consumption.',
              icon: CloudMonitoring,
            },
            {
              title: 'Day Care Centres',
              description: 'Ensure safe food storage temperatures for children\'s meals. Monitor room conditions for optimal comfort and safety.',
              icon: MobileCheck,
            },
            {
              title: 'Home Fridges',
              description: 'Keep your family safe by monitoring refrigerator and freezer temperatures. Get alerts if temperatures rise above safe levels.',
              icon: BatteryFull,
            },
            {
              title: 'Cooling Gazebos',
              description: 'Perfect for trips and outdoor events. Monitor portable cooling units to ensure food and beverages stay at safe temperatures during camping, picnics, and gatherings.',
              icon: ChartLine,
            },
            {
              title: 'Food Safety',
              description: 'Comprehensive monitoring for restaurants, cafes, and food service establishments. Ensure HACCP compliance and prevent foodborne illnesses.',
              icon: CheckmarkFilled,
            },
            {
              title: 'Fresh Produce Packaging',
              description: 'Monitor temperature and humidity during packaging and storage of fresh fruits and vegetables. Extend shelf life and maintain quality from farm to consumer.',
              icon: DataBase,
            },
          ].map((application, index) => (
            <Column lg={5} md={4} sm={4} key={index}>
              <Tile
                className="feature-card"
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  padding: '32px',
                  height: '100%',
                }}
                data-testid={`application-${application.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <application.icon size={28} style={{ color: '#ffffff' }} />
                </div>
                <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600 }}>
                  {application.title}
                </h3>
                <p style={{ color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
                  {application.description}
                </p>
              </Tile>
            </Column>
          ))}
        </Grid>
      </section>

<section className="home-split-section" data-testid="home-split-section" aria-label="Quick actions">
        <button
          type="button"
          className="home-split-panel home-split-panel--primary"
          onClick={() => navigate('/signup')}
          data-testid="home-split-monitor"
        >
          <CloudMonitoring size={32} className="home-split-panel__icon" aria-hidden />
          <span className="home-split-panel__label">Start monitoring your cold chain</span>
          <span className="home-split-panel__desc">
            Create zones, set alert rules and watch live temperature and humidity from one dashboard.
          </span>
          <span className="home-split-panel__cta">
            Get started free
            <ArrowRight size={20} className="home-split-panel__arrow" aria-hidden />
          </span>
        </button>
        <button
          type="button"
          className="home-split-panel home-split-panel--secondary"
          onClick={() => navigate('/shop')}
          data-testid="home-split-shop"
        >
          <ShoppingCart size={32} className="home-split-panel__icon" aria-hidden />
          <span className="home-split-panel__label">Sensors, gateways &amp; integrations</span>
          <span className="home-split-panel__desc">
            Browse supported loggers, BLE beacons and gateways built for ChillVerify monitoring.
          </span>
          <span className="home-split-panel__cta">
            Shop hardware
            <ArrowRight size={20} className="home-split-panel__arrow" aria-hidden />
          </span>
        </button>
      </section>

<section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }} data-testid="how-can-help-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="high-contrast" style={{ marginBottom: '8px' }}>Value Proposition</Tag>
              <h2 style={{ color: '#ffffff', marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                How can ChillVerify help?
              </h2>
            </div>
          </Column>
        </Grid>
        <Grid>
          <Column lg={5} md={4} sm={4}>
            <Tile style={{ backgroundColor: bgPrimary, padding: '32px', height: '100%' }}>
              <div style={{ backgroundColor: '#24a148', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <ChartLine size={32} style={{ color: '#ffffff' }} />
              </div>
              <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                Cost Savings
              </h3>
              <ul style={{ color: textSecondary, fontSize: '0.875rem', paddingLeft: '0', margin: 0, listStyle: 'none' }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Reduce product spoilage</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Avoid costly recalls</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Energy optimization</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Lower insurance premiums</span>
                </li>
              </ul>
            </Tile>
          </Column>
          <Column lg={5} md={4} sm={4}>
            <Tile style={{ backgroundColor: bgPrimary, padding: '32px', height: '100%' }}>
              <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Certificate size={32} style={{ color: '#ffffff' }} />
              </div>
              <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                Revenue Protection
              </h3>
              <ul style={{ color: textSecondary, fontSize: '0.875rem', paddingLeft: '0', margin: 0, listStyle: 'none' }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Customer confidence</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Regulatory compliance</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Quality assurance</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Brand reputation</span>
                </li>
              </ul>
            </Tile>
          </Column>
          <Column lg={6} md={4} sm={4}>
            <Tile style={{ backgroundColor: bgPrimary, padding: '32px', height: '100%' }}>
              <div style={{ backgroundColor: '#6929c4', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Analytics size={32} style={{ color: '#ffffff' }} />
              </div>
              <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                Operational Efficiency
              </h3>
              <ul style={{ color: textSecondary, fontSize: '0.875rem', paddingLeft: '0', margin: 0, listStyle: 'none' }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Real-time alerts</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Automated logging</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Route optimization</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                  <span>Data-driven decisions</span>
                </li>
              </ul>
            </Tile>
          </Column>
        </Grid>
      </section>

<section style={{ backgroundColor: bgPrimary, padding: '80px 0' }} data-testid="ai-intelligence-section">
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <img
                loading="lazy"
                src="/assets/ai-white.webp"
                alt="ChillVerifyAI"
                style={{
                  maxWidth: '280px',
                  height: 'auto',
                  filter: isDark ? 'none' : 'invert(1)',
                }}
                data-testid="ai-intelligence-image"
              />
            </div>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <div style={{ paddingLeft: '48px' }}>
              <Tag type="magenta">ChillVerifyAI</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', marginBottom: '24px', fontSize: '2.5rem', fontWeight: 600 }}>
                Maximise cold chain intelligence with ChillVerifyAI
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '1.125rem', lineHeight: 1.8 }}>
                Use AI to your strength - analyse trends, make predictions, and generate reports using your local AI assistant. Work simpler and more efficiently with seamless analysis while keeping your data private.
              </p>
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ color: textPrimary, marginBottom: '16px', fontWeight: 600, fontSize: '1.25rem' }}>Unlock Insights from Your Data</h4>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '1rem', lineHeight: 1.6 }}>
                  Get patterns, trends, and actionable insights from your temperature and humidity data. Add context and make the most of artificial intelligence to improve interpretation and decision-making.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Analyse patterns and trends from historical data',
                    'Generate comprehensive reports with AI assistance',
                    'Predict potential issues before they occur',
                    'Local AI processing - your data stays with you',
                    'Natural language queries for easy data exploration',
                    'Automated insights and recommendations',
                  ].map((feature, index) => (
                    <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckmarkFilled size={20} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ color: textSecondary, fontSize: '0.9375rem', lineHeight: 1.6 }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Button kind="primary" size="lg" onClick={() => navigate('/signup')} data-testid="ai-get-started">
                  Get Started
                </Button>
                <Button kind="tertiary" size="lg" onClick={() => navigate('/features')} data-testid="ai-learn-more">
                  Learn More <ArrowRight style={{ marginLeft: '8px' }} />
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

<section style={{ backgroundColor: bgSecondary, padding: '80px 0' }} data-testid="chillverify-flow-section">
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <div style={{ paddingRight: '48px' }}>
              <Tag type="purple">ChillVerify Flow</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', marginBottom: '24px', fontSize: '2.5rem', fontWeight: 600 }}>
                AI-powered data pipelines for third-party integrations
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '1.125rem', lineHeight: 1.8 }}>
                Build visual data pipelines that route, transform and deliver sensor readings to ERPs, webhooks, MQTT
                endpoints and the systems your team already relies on — with AI-assisted setup.
              </p>
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ color: textPrimary, marginBottom: '16px', fontWeight: 600, fontSize: '1.25rem' }}>
                  Connect your cold chain to everything else
                </h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Visual drag-and-drop pipeline editor',
                    'Stream gateway and sensor data to third-party platforms',
                    'MQTT, HTTP webhooks and custom transforms',
                    'AI-assisted mapping and data routing',
                    'Live pipeline health and connectivity monitoring',
                    'Keep compliance data flowing to external tools automatically',
                  ].map((feature, index) => (
                    <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckmarkFilled size={20} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ color: textSecondary, fontSize: '0.9375rem', lineHeight: 1.6 }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Button kind="primary" size="lg" onClick={() => navigate('/signup')} data-testid="flow-get-started">
                  Get Started
                </Button>
                <Button kind="tertiary" size="lg" onClick={() => navigate('/features')} data-testid="flow-learn-more">
                  Learn More <ArrowRight style={{ marginLeft: '8px' }} />
                </Button>
              </div>
            </div>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: '320px',
              }}
              data-testid="chillverify-flow-visual"
            >
              <div
                style={{
                  backgroundColor: bgPrimary,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '16px',
                  width: '100%',
                  maxWidth: '420px',
                  padding: '48px 32px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    backgroundColor: '#0f62fe',
                    borderRadius: '20px',
                    width: '120px',
                    height: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <PipelineIcon size={64} style={{ color: '#ffffff' }} />
                </div>
                <p style={{ color: textPrimary, fontWeight: 600, fontSize: '1.125rem', marginBottom: '8px' }}>
                  ChillVerify Flow
                </p>
                <p style={{ color: textSecondary, fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  Sensors → transform → ERP, webhooks, MQTT &amp; more
                </p>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

<section style={{ backgroundColor: bgDark, padding: '80px 0' }} data-testid="core-features-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 style={{ color: textPrimary, textAlign: 'center', marginBottom: '48px', fontSize: '2rem', fontWeight: 600 }}>
              Core Features
            </h2>
          </Column>
        </Grid>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '24px',
            justifyContent: 'center'
          }}>
            {mainFeatures.map((feature, index) => (
              <Tile
                key={index}
                className="feature-card"
                style={{
                  backgroundColor: bgPrimary,
                  border: `1px solid ${borderColor}`,
                  padding: '32px',
                  height: '100%',
                }}
                data-testid={`feature-${index}`}
              >
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <feature.icon size={28} style={{ color: '#ffffff' }} />
                </div>
                <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600 }}>
                  {feature.title}
                </h3>
                <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                  {feature.description}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {feature.details.map((detail, idx) => (
                    <li key={idx} style={{ color: textSecondary, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <div style={{ width: '4px', height: '4px', backgroundColor: '#0f62fe', borderRadius: '50%' }} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </Tile>
            ))}
          </div>
        </div>
      </section>

<section style={{ backgroundColor: bgSecondary, padding: '80px 0' }} data-testid="benefits-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="cyan">Why Choose Us</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                Benefits
              </h2>
              <p style={{ color: textSecondary, maxWidth: '600px', margin: '16px auto 0' }}>
                What we provide to help you succeed
              </p>
            </div>
          </Column>
          {benefits.map((benefit, index) => (
            <Column lg={4} md={4} sm={4} key={index}>
              <Tile
                className="feature-card"
                style={{
                  backgroundColor: bgPrimary,
                  border: `1px solid ${borderColor}`,
                  padding: '32px',
                  height: '100%',
                }}
                data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <benefit.icon size={24} style={{ color: '#ffffff' }} />
                </div>
                <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600 }}>
                  {benefit.title}
                </h3>
                <p style={{ color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6 }}>{benefit.desc}</p>
              </Tile>
            </Column>
          ))}
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Button kind="primary" onClick={() => navigate('/contact')} data-testid="contact-us-btn">Contact Us</Button>
            </div>
          </Column>
        </Grid>
      </section>

<section style={{ backgroundColor: bgDark, padding: '80px 0' }} data-testid="about-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ color: textPrimary, marginBottom: '24px', fontSize: '2.5rem', fontWeight: 600 }}>
                About Us
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '1rem', lineHeight: 1.8 }}>
                ChillVerify is a comprehensive platform for sensor analytics, monitoring, and data visualization. 
                We help teams capture reliable temperature and humidity insights across logistics, storage, and operations.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'inline-block', textAlign: 'left' }}>
                {['Real-time environmental monitoring', 'AI-powered insights and predictions', 'Compliance reporting', 'Multi-platform support'].map((item, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: textSecondary }}>
                    <CheckmarkFilled size={20} style={{ color: '#24a148' }} />
                    {item}
                  </li>
                ))}
              </ul>
              <div>
                <Button kind="primary" onClick={() => navigate('/login')} data-testid="about-get-started">Get Started</Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default LandingPage;
